
import { useState, useContext } from 'react';
import SessionContext from "SessionContext";
import { states } from "../core/utils/list-of-states";

/**
 * filter with range values are removed
 * v-epsln-demog-000030: Age Range
 * v-epsln-demog-000031: Year person was born
 * v-epsln-demog-000033: Date Of Birth
 */
const range_filters = ["v-epsln-demog-000030", "v-epsln-demog-000031", "v-epsln-demog-000033"];
const isModelRegex = /^model_/;

// look up the state full name given the abbreviation
const getStateInfo = (_state) => {
  const state = states.find(state => state.code === _state);
  return state ? state.name : _state;
} 

/**
 * 
 * @param run pass 'true' to make it run
 * @returns 'all_filters' 'filters_labels' 'all_models' 'models_labels'
 * in array format
 */
async function useFiltersInfo(run = false) {
  const session = useContext(SessionContext);
  const allFilters = session.crossfilterStore.filters;
  const allInsights = session.crossfilterStore.widgets;
  const allComplex = session.crossfilterStore.complexFilters;
  const allCharts = session.chartsStore.charts;
  const [singleFilterData, setSingleFilterData] = useState("");

  const prefix = session.crossfilterStore.geoPrefix;
  const geo_filters = ["county_fips", "cbsa", "congress", `${prefix}county_fips`, `${prefix}cbsa`, `${prefix}congress`];
  const apiSearchEndpoints = {
    county_fips: "location/get-county-names-map",
    congress: "location/get-cd-names-map",
    cbsa: "location/get-cbsa-names-map",
    [`${prefix}county_fips`]: "location/get-county-names-map",
    [`${prefix}congress`]: "location/get-cd-names-map",
    [`${prefix}cbsa`]: "location/get-cbsa-names-map",
  };

  /* generate all data from complex filters */
  let all_complex_filters = [];
  if( allComplex && Object.keys(allComplex).length ) {
    let filter_entries = Object.entries(allComplex.tree.children1) || [];
    filter_entries && filter_entries.length && filter_entries.forEach((item) => {      
      const runSimpleQuery = (item) => {
        let _ds = item.properties.field.split("__")[0];
        let _id = item.properties.field.split("__")[1];
        let _label = allCharts[_id].label;
        let _code_values = item.properties.value; // list of selected code values
        let _value_labels = allCharts[_id].value_labels;
        let _values = {};
        let _friendly_names = {};
        _code_values.forEach((code) => {
          code && code.length && code.forEach((val) => {
            if(Object.keys(_value_labels).length) {
              _values[`${val}`] = _value_labels[val];
            } else {
              _values[`${val}`] = `${val}`;
            }
          })
        });
        if(_id === `${prefix}state`) {
          let _temp = {};
          Object.entries(_values).forEach((_val) => {
            let full_name = getStateInfo(_val[0]) || _val[0];
            _temp[_val[0]] = full_name ;
          })
          _friendly_names = _temp;
        } 
  
        // update filters_only if conditions are met
        if (_ds && _id && Object.keys(_values).length && !range_filters.includes(_id)) {
          all_complex_filters.push({
            ds: _ds,
            id: _id,
            label: _label,
            friendly_names: _friendly_names,
            values: JSON.parse(JSON.stringify(_values)),
          });
        }
      }

      // recursive to find all children1
      const findChildren1 = (item) => {
        let propertiesArray = [];
        for (const key in item) {
          if (item[key].hasOwnProperty('properties') && item[key].properties.hasOwnProperty('field')) {
            propertiesArray.push(item[key].properties);
          }
          if (item[key].hasOwnProperty('children1')) {
            const childProperties = findChildren1(item[key].children1);
            if (childProperties && childProperties.length > 0) {
              propertiesArray = propertiesArray.concat(childProperties);
            }
          }
        }
        propertiesArray.forEach((val) => {
          runSimpleQuery({properties: val}); 
        }) 
      }

      if( !('children1' in item[1]) ) {
        runSimpleQuery(item[1]);
      } else {
        findChildren1(item[1].children1);
      }

    })
  }

  function getGeoLabels() {
    return new Promise(resolve => {
      let ready_to_resolve = false;
      all_complex_filters.forEach((item, index) => {
        if(geo_filters.includes(item.id)) {
          let all_values = Object.keys(item.values);
          session.command(apiSearchEndpoints[item.id], all_values)
            .then((response) => {
              item.friendly_names = response;
            })
        }
        if(index === all_complex_filters.length - 1) {
          ready_to_resolve = true;
        }
      })
      if(ready_to_resolve) {
        resolve(true);
      }
    });
  }


  if(all_complex_filters.length) {
    await getGeoLabels();
  }

  let all_selected_filters = Object.entries(allFilters) || [];
  let all_selected_insights = allInsights || [];
  let all_selected_models = [];

  /* if complex: generate models info */
  if( allComplex && Object.keys(allComplex).length ) {
    (all_complex_filters && Object.keys(all_complex_filters).length) && (
      Object.entries(all_complex_filters).forEach((val) => {
        if(isModelRegex.test(val[1].id)) {
          let add_filter = [val[1].id, [val[1]]];
          all_selected_filters.push(add_filter);
        }
      })
    )
  } 

  /* generate all the models info */
  all_selected_filters.length && all_selected_filters.forEach((_filter) => {
    if(isModelRegex.test(_filter[0]) && allCharts[_filter[0]]) {
      let _label = allCharts[_filter[0]].label;
      let _id = allCharts[_filter[0]].id;
      let _ds = allCharts[_filter[0]].ds;
      let _temp = {ds: _ds, id: _id, label: _label};
      all_selected_models.push(_temp);
    }
  });

  all_selected_insights.length && all_selected_insights.forEach((_insight) => {
    if(isModelRegex.test(_insight)) {
      let _label = allCharts[_insight].label;
      let _id = allCharts[_insight].id;
      let _ds = allCharts[_insight].ds;
      let _temp = {ds: _ds, id: _id, label: _label};
      all_selected_models.push(_temp);
    }
  });

  let filters_only = [];

  if (all_selected_filters.length) {
    all_selected_filters.forEach((_filter) => {
      if (!isModelRegex.test(_filter[0]) && allCharts[_filter[0]]) {
        let _label = allCharts[_filter[0]].label;
        let _id = allCharts[_filter[0]].id;
        let _ds = allCharts[_filter[0]].ds;
        let all_values = allCharts[_filter[0]].value_labels || {};
        let _friendly_name = {};
        let _values = _filter[1].every(item => item.value in all_values) ? _filter[1].reduce((acc, item) => {
            acc[item.value] = all_values[item.value];
            return acc;
          }, {})
        : {};

        // try to populate _values if it's empty
        if (allFilters[_id] && !Object.keys(_values).length) {
          allFilters[_id].forEach((val) => {
            _values[val.value] = val.value;
            _friendly_name[val.value] = val.friendly_name;
          });
        }

        // get friendly names from geo widgets
        if(_id.includes("county_fips") || _id.includes("congress") || _id.includes("cbsa") || _id.includes("state") ) {
          if(_filter[1]) {
            _filter[1].forEach((item) => {
              if(item.friendly_name) {
                _friendly_name[item.value] = item.friendly_name.replace(/ - $/, "");
              } else {
                _friendly_name[item.value] = getStateInfo(item.value);
              }
            })
          }
        }

        // update filters_only if conditions are met
        if (_ds && _id && Object.keys(_values).length && !range_filters.includes(_id)) {
          filters_only.push({
            ds: _ds,
            id: _id,
            label: _label,
            friendly_names: JSON.parse(JSON.stringify(_friendly_name)),
            values: JSON.parse(JSON.stringify(_values)),
          });
        }
      }
    });
  }

  /* generate data from insights */
  all_selected_insights.length && all_selected_insights.forEach((_insight) => {
    if(!isModelRegex.test(_insight)) {
      let _label = allCharts[_insight].label;
      let _id = allCharts[_insight].id;
      let _ds = allCharts[_insight].ds;
      let _values = allCharts[_insight].value_labels;
      // insights without values and range filters are skipped 
      if(_ds && _id && _values && !range_filters.includes(_id)) {
        let _temp = {ds: _ds, id: _id, label: _label, values: _values, is_insight: true};
        filters_only.push(_temp);
      }
    }
  })

  /* remove elements without values */
  filters_only = filters_only.filter(item => Object.keys(item.values).length > 0);

  /* remove the values not selected or negated */
  const filterOnlyLookup = Object.fromEntries(
    filters_only.map(item => [item.id, item])
  );
 
  all_selected_filters.forEach(([filterId, filterValues]) => {
    if (filterOnlyLookup[filterId]) {
      const currentFilter = filterOnlyLookup[filterId];
  
      const filteredValues = Object.fromEntries(
        Object.entries(currentFilter.values).filter(([key]) =>
          filterValues.some(fv => fv.value === key)
        )
      );
  
      if (Object.keys(filteredValues).length) {
        currentFilter.values = filteredValues;
      } else {
        delete filterOnlyLookup[filterId];
      }
    }
  });
  
  let filters_only_cleaned = [];
  filters_only.forEach((_val) => {
    if (Object.keys(_val.values).length !== 0) {
      filters_only_cleaned.push(_val);
    }
  })

  /* if it is complex: use the complex filters */
  if(allComplex && Object.keys(allComplex).length) {
    if(all_selected_models && all_selected_models.length) {
      // remove models from filters list
      let models_labels = all_selected_models.map(model => model.label);
      let no_model_filters = all_complex_filters.filter(filter => !models_labels.includes(filter.label));
      filters_only = no_model_filters;
    } else {
      filters_only = all_complex_filters;
    }
  }

  /* util to remove duplicates */
  const removeDuplicates = (array_list) => {
    let no_dups = Object.values(
      array_list.reduce((acc, current) => {
        const id = current.id;
        if (!acc[id]) {
          acc[id] = current;
        } else {
          // when duplicates are found usually are insights and filters, 
          // discard the insight since the filter has the selections we need
          const existingHasInsight = 'is_insight' in acc[id];          
          const currentHasInsight = 'is_insight' in current;
          if (!currentHasInsight && existingHasInsight) {
            acc[id] = current;
          }
        }
        return acc;
      }, {})
    );
    return no_dups;
  }

  /* remove duplicate filters */
  let filters_only_unique = removeDuplicates(filters_only); 

  /* remove duplicate models */
  let models_only_unique = removeDuplicates(all_selected_models); 

  /* collect all filter labels */
  let all_filters_labels = [];
  filters_only_unique.length && filters_only_unique.forEach((_item) => {
    all_filters_labels.push(_item.label);
  })

  /* collect all model labels */
  let all_models_labels = [];
  models_only_unique.length && all_selected_models.forEach((_item) => {
    all_models_labels.push(_item.label);
  })

  /* return data for specific filter */
  const getFilterData = (filter_id) => {    
    let find_filter = filters_only_unique.filter(item => item.id === filter_id);
    if(JSON.stringify(singleFilterData) !== JSON.stringify(find_filter)) {
      setSingleFilterData(find_filter);
    }
  }

  if(run) {
    return { 
      all_filters: filters_only_unique,
      all_models: models_only_unique,
      all_complex: all_complex_filters,
      filters_labels: all_filters_labels,
      models_labels: all_models_labels,
      getFilterData,
      singleFilterData,
    };
  }

  return () => ({});
}

export default useFiltersInfo;